import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'
import { Screenshot } from '../../components/DocsComponents'

const Reports = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">What PTS Reports is for</Typography>
      <Typography>
        <DocLink url="/pts/reports" text="PTS Reports" /> shows journey-level
        on-time performance for a date range: how quickly crews reached
        patients, how often destinations were hit on time, average time spent
        at each stage of a journey, and cancellation/no-show/aborted counts.
      </Typography>
      <Typography>
        Filter by date range and, optionally, one organisation. Click any row
        to see every recorded time for that journey — Mobile, On Scene,
        Patient Onboard, Arrived, and Handover Complete.
      </Typography>
      <Screenshot url="patient-transport/reports.png">
        PTS Reports showing the on-time pickup/arrival stat tiles, the average
        stage-duration bars, and the journey grid below.
      </Screenshot>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">On-time definitions</Typography>
      <Typography>
        <strong>On-time to patient</strong> compares when the crew went{' '}
        <strong>On Scene</strong> to the patient's ready-from time, allowing a
        configurable window (default 60 minutes) — arriving early always
        counts as on time. <strong>On-time to destination</strong> compares
        <strong> Arrived</strong> to the journey's target arrival time, with a
        configurable grace period (default none). Both windows are adjustable
        on the report screen itself, so you can re-run the same period against
        a stricter or looser definition without changing any setting
        permanently.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Portal-booking share and SLA targets</Typography>
      <Typography>
        The same underlying report also calculates what share of a period's
        journeys came from the hospital booking portal rather than being
        phoned or emailed in, and — when a single organisation is selected and
        it has SLA targets configured — a set of met/missed comparisons
        against those targets. This is exactly what powers the{' '}
        <strong>SLA &amp; Performance</strong> section on an organisation's own
        profile page; see{' '}
        <DocLink
          url="/patient-transport/organisations"
          text="Organisations and Users"
          docsPage
        />{' '}
        for where to set the targets and download a performance-pack PDF for
        one organisation.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Delay reasons</Typography>
      <Typography>
        Delays reported by crew (see{' '}
        <DocLink url="/patient-transport/crew-view" text="Crew View" docsPage />
        ) are recorded against a fixed set of reason codes — Traffic, Ward
        Delay, Patient Not Ready, Equipment Issue, Vehicle Fault, Crew Break,
        Awaiting Handover, Prior Job Overran, or Other — so recurring causes
        show up clearly instead of being lost across many differently-worded
        free-text notes.
      </Typography>
      <Alert severity="info" variant="outlined">
        Journeys delayed before this reason-code list existed only recorded a
        free-text note. Those older delays still count towards the total, but
        group by their original wording rather than a code.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Waiting time on invoices</Typography>
      <Typography>
        Where a tariff charges for waiting time, the invoice line quotes
        exactly how long the crew waited at pickup and at handover, naming the
        ward or address for each, and marks a segment{' '}
        <strong>GPS-verified</strong> when the crew's location was actually
        captured at that point. See{' '}
        <DocLink
          url="/patient-transport/invoicing"
          text="Quoting, Invoicing & Billing"
          docsPage
        />{' '}
        for how tariffs and auto-pricing work.
      </Typography>
    </Stack>
  </Stack>
)

export default Reports
